import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { NODE_THEMES } from './NodeTheme';
import { NodeFormProvider } from './NodeFormKit';

const resolveTheme = (isDark) =>
  isDark === 'true' || isDark === true ? NODE_THEMES.dark : NODE_THEMES.light;

export const NodeModalShell = ({
  icon: Icon,
  color = '#5BC0DE',
  eyebrow,
  title,
  onClose,
  isDark,
  footer,
  bodyStyle,
  children,
}) => {
  const theme = resolveTheme(isDark);
  const formContext = React.useMemo(() => ({ theme, color }), [theme, color]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        backgroundColor: theme.panelBg,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: '64px',
          padding: '0 10px 0 20px',
          borderBottom: `1px solid ${theme.border}`,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: '19px',
            fontWeight: 600,
            color: theme.textPrimary,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </div>
        {eyebrow && (
          <span
            style={{
              flexShrink: 0,
              padding: '2px 8px',
              borderRadius: '99px',
              backgroundColor: theme.inputBg,
              color: theme.textSecondary,
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {eyebrow}
          </span>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '38px',
              height: '38px',
              flexShrink: 0,
              padding: 0,
              border: 'none',
              borderRadius: '10px',
              backgroundColor: 'transparent',
              color: theme.textPrimary,
              cursor: 'pointer',
            }}
          >
            <IoClose style={{ fontSize: '18px' }} />
          </button>
        )}
      </div>

      <div
        className="node-editor-body"
        style={{
          flex: 1,
          minHeight: 0,
          padding: '18px 20px',
          overflowY: 'auto',
          backgroundColor: theme.panelBg,
          color: theme.textPrimary,
          ...bodyStyle,
        }}
      >
        <NodeFormProvider value={formContext}>{children}</NodeFormProvider>
      </div>

      {footer && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '12px 20px',
            borderTop: `1px solid ${theme.border}`,
            backgroundColor: theme.panelBg,
            flexShrink: 0,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export const NodeModalButton = ({
  onClick,
  color,
  isDark,
  variant = 'secondary',
  type = 'button',
  disabled = false,
  form,
  children,
}) => {
  const theme = resolveTheme(isDark);
  const primary = variant === 'primary';
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '9px 15px',
        fontSize: '13.5px',
        fontWeight: 600,
        borderRadius: '9px',
        border: primary ? 'none' : `1px solid ${theme.borderStrong || theme.border}`,
        backgroundColor: primary ? '#0a7ba5' : 'transparent',
        color: primary ? '#FFFFFF' : theme.textSecondary,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

const NodeModal = ({
  open,
  onClose,
  anchorEl,
  width = 520,
  isDark,
  ...shellProps
}) => {
  const theme = resolveTheme(isDark);

  useEffect(() => {
    if (!open || anchorEl) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, anchorEl, onClose]);

  const surface = {
    width,
    maxWidth: '100%',
    overflow: 'hidden',
    borderRadius: '16px',
    backgroundColor: theme.panelBg,
    border: `1px solid ${theme.border}`,
    boxShadow: '0 14px 36px -12px rgba(0, 0, 0, 0.35)',
  };

  if (anchorEl) {
    return (
      <Popover
        open={Boolean(open)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            style: {
              ...surface,
              display: 'flex',
              maxHeight: 'min(70vh, 560px)',
            },
          },
        }}
      >
        <NodeModalShell {...shellProps} isDark={isDark} onClose={onClose} />
      </Popover>
    );
  }

  if (!open) return null;

  return createPortal(
    <div
      className="su-kit"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
      }}
    >
      <div style={{ ...surface, maxHeight: 'calc(100vh - 64px)', display: 'flex' }}>
        <NodeModalShell {...shellProps} isDark={isDark} onClose={onClose} />
      </div>
    </div>,
    document.body
  );
};

export default NodeModal;
